import { useCallback, useRef } from 'react';

/**
 * Hook for generating touch event handlers that will call any provided swipe-triggered functions
 * @param {String} handlers - object containing swipe event handlers:
 * onSwipeUp, onSwipeDown, onSwipeLeft, onSwipeRight
 * @param {String} threshold - number of pixels between touchStart and touchEnd that must be
 * exceeded to be considered a swipe
 * @returns {Object} containing touch event handlers
 */
const useSwipeHandlers = (handlers = {}, threshold = 100) => {
  const mouseDownCoords = useRef(null);
  const mouseUpCoords = useRef(null);
  const onTouchStart = useCallback((e) => {
    mouseUpCoords.current = null;
    mouseDownCoords.current = [e.targetTouches[0]?.clientX, e.targetTouches[0]?.clientY];
  }, []);

  const onTouchMove = useCallback((e) => {
    mouseUpCoords.current = [e.targetTouches[0]?.clientX, e.targetTouches[0]?.clientY];
  }, []);

  const onTouchEnd = useCallback(() => {
    if (mouseUpCoords.current && mouseDownCoords.current) {
      const xDiff = mouseDownCoords.current[0] - mouseUpCoords.current[0];
      const yDiff = mouseDownCoords.current[1] - mouseUpCoords.current[1];

      // only swipe up/down if y change exceeded x change, or vice versa for left/right
      if (Math.abs(yDiff) > Math.abs(xDiff)) {
        if (yDiff > threshold && handlers.onSwipeUp) {
          handlers.onSwipeUp();
        } else if (yDiff < (0 - threshold) && handlers.onSwipeDown) {
          handlers.onSwipeDown();
        }
      } else if (Math.abs(xDiff) > Math.abs(yDiff)) {
        if (xDiff > threshold && handlers.onSwipeLeft) {
          handlers.onSwipeLeft();
        } else if (xDiff < (0 - threshold) && handlers.onSwipeRight) {
          handlers.onSwipeRight();
        }
      }
    }
  }, [handlers, threshold]);

  return { onTouchStart, onTouchMove, onTouchEnd };
};

export default useSwipeHandlers;
